import { Link } from "@/lib/router-compat";
import type {
  LearnFAQ,
  LearnPageData,
  LearnSection,
  LearnSource,
} from "@/pages/learn/learnShared";
import {
  SITE_URL,
  orgNode,
  faqJsonld,
  breadcrumbJsonld,
  webPageNode,
} from "@/pages/learn/learnShared";
import { esBaseCrumbs } from "./learnFactoryEs";
import type { RelatedLink as RL } from "@/components/RelatedPages";

/**
 * /es/learn/saint-disclaimer
 *
 * Versi\u00f3n en espa\u00f1ol de /learn/saint-disclaimer (PROMPT 9).
 * Aviso claro y citable sobre SAINT\u00ae: Cognitive FX no es SAINT\u00ae, no
 * est\u00e1 afiliada y los resultados de SAINT\u00ae no se aplican a ella.
 * Cada afirmaci\u00f3n sobre Cognitive FX es propia de su sitio web (citada,
 * enlazada) o un hecho p\u00fablico verificable. Las citas cl\u00ednicas se
 * conservan en ingl\u00e9s porque provienen de una fuente en ingl\u00e9s.
 */

const reviewBlock = {
  writtenBy: "Neuro Wellness TMS Centers of America",
  reviewedBy: "Daniel Pietras, MD",
  reviewedByHref: "/dr-daniel-pietras-md",
  published: "3 de noviembre de 2026",
  lastReviewed: "3 de noviembre de 2026",
  lastUpdated: "3 de noviembre de 2026",
};

const quotes: { text: string; url: string }[] = [
  {
    text: "Cognitive FX does not provide SAINT\u2122",
    url: "https://www.cognitivefxusa.com/blog/saint-tms-locations",
  },
  {
    text: "our own protocol, delivered off-label",
    url: "https://www.cognitivefxusa.com/blog/saint-treatment-cost",
  },
  {
    text: "rather than using Magnus Medical\u2019s proprietary targeting system",
    url: "https://www.cognitivefxusa.com/blog/saint-treatment-cost",
  },
  {
    text: "our protocol has not been tested in the SAINT clinical trials",
    url: "https://www.cognitivefxusa.com/blog/saint-treatment-cost",
  },
  {
    text: "CFX\u2019s program is an off-label equivalent of the SAINT protocol, not the licensed Magnus SAINT\u2122 product",
    url: "https://www.cognitivefxusa.com/blog/brain-before-and-after-tms",
  },
  {
    text: "Insurance Not billed",
    url: "https://www.cognitivefxusa.com/precision-mental-health-fmri-guided-transcranial-magnetic-stim",
  },
  {
    text: "Cognitive FX does not provide SAINT\u00ae and is not affiliated with, endorsed by or licensed by Stanford University or Magnus Medical.",
    url: "https://www.cognitivefxusa.com/precision-mental-health-fmri-guided-transcranial-magnetic-stim",
  },
  {
    text: "does not use Magnus Medical equipment or software",
    url: "https://www.cognitivefxusa.com/blog/tms-vs.-psilocybin-therapy-for-depression-what-the-science-says-2026",
  },
];

const sources: LearnSource[] = [
  {
    citation:
      "Cognitive FX. SAINT TMS Locations. Publicado por Cognitive FX, Provo, UT. Consultado el 3 de noviembre de 2026.",
    url: "https://www.cognitivefxusa.com/blog/saint-tms-locations",
  },
  {
    citation:
      "Cognitive FX. SAINT Treatment Cost: How Does It Compare to Other Accelerated TMS? Publicado por Cognitive FX, Provo, UT. Consultado el 3 de noviembre de 2026.",
    url: "https://www.cognitivefxusa.com/blog/saint-treatment-cost",
  },
  {
    citation:
      "Cognitive FX. Brain Before and After TMS. Publicado por Cognitive FX, Provo, UT. Consultado el 3 de noviembre de 2026.",
    url: "https://www.cognitivefxusa.com/blog/brain-before-and-after-tms",
  },
  {
    citation:
      "Cognitive FX. Precision Mental Health: fMRI-Guided Transcranial Magnetic Stimulation. Publicado por Cognitive FX, Provo, UT. Consultado el 3 de noviembre de 2026.",
    url: "https://www.cognitivefxusa.com/precision-mental-health-fmri-guided-transcranial-magnetic-stim",
  },
  {
    citation:
      "Cognitive FX. TMS vs. Psilocybin Therapy for Depression: What the Science Says (2026). Publicado por Cognitive FX, Provo, UT. Consultado el 3 de noviembre de 2026.",
    url: "https://www.cognitivefxusa.com/blog/tms-vs.-psilocybin-therapy-for-depression-what-the-science-says-2026",
  },
  {
    citation:
      "Cole EJ, et al. Stanford Neuromodulation Therapy (SNT): A Double-Blind Randomized Controlled Trial. Am J Psychiatry. 2022;179(2):132-141.",
    url: "https://pubmed.ncbi.nlm.nih.gov/35624717/",
  },
  {
    citation:
      "U.S. Food and Drug Administration. 510(k) Premarket Notification \u2014 Magnus Neuromodulation System with SAINT Technology (K220177, septiembre de 2022).",
    url: "https://www.accessdata.fda.gov/scripts/cdrh/cfdocs/cfpmn/pmn.cfm",
  },
];

const faqs: LearnFAQ[] = [
  {
    question: "\u00bfCognitive FX es SAINT\u00ae?",
    answer:
      "No. El propio sitio web de Cognitive FX afirma que no ofrece SAINT\u2122 y describe su programa como su propio protocolo, aplicado off-label, no probado en los ensayos cl\u00ednicos de SAINT.",
  },
  {
    question:
      "\u00bfPuedo esperar los resultados de SAINT\u00ae de la TMS guiada por fMRI de Cognitive FX?",
    answer:
      "No. Los resultados publicados de SAINT provienen solo de SAINT\u00ae en el sistema Magnus autorizado por la FDA, y Cognitive FX afirma que su protocolo no se prob\u00f3 en los ensayos de SAINT, por lo que esos resultados no se trasladan.",
  },
  {
    question:
      "\u00bfCognitive FX est\u00e1 afiliada con SAINT\u00ae, Stanford, Magnus Medical o Neuro Wellness?",
    answer:
      "No. Cognitive FX no tiene ninguna asociaci\u00f3n con SAINT\u00ae, Stanford University, Magnus Medical ni Neuro Wellness; su propio sitio web afirma que no est\u00e1 afiliada, avalada ni autorizada por Stanford ni Magnus y que no usa equipos ni software de Magnus.",
  },
  {
    question:
      "\u00bfC\u00f3mo s\u00e9 si una cl\u00ednica ofrece SAINT\u00ae real?",
    answer:
      "Consulte el localizador oficial de proveedores de Magnus Medical; en Florida, el proveedor verificado es Neuro Wellness TMS Centers of America en Coral Springs.",
  },
];

const sections: LearnSection[] = [
  {
    heading: "La versi\u00f3n breve",
    body: (
      <ul className="mt-4 space-y-2.5">
        {[
          "Cognitive FX no ofrece SAINT\u00ae. Su programa de 5 d\u00edas de \u201cTMS acelerada guiada por fMRI\u201d es un tratamiento distinto.",
          "No existe ninguna asociaci\u00f3n entre Cognitive FX y SAINT\u00ae, Magnus Medical, Stanford University o Neuro Wellness TMS Centers of America. Cognitive FX afirma que \u201cno est\u00e1 afiliada, avalada ni autorizada por Stanford University ni Magnus Medical\u201d y que \u201cno usa equipos ni software de Magnus Medical.\u201d",
          "Los resultados publicados de SAINT (incluido el 78,6 % de remisi\u00f3n en el ensayo controlado aleatorizado de 2022) provienen \u00fanicamente de SAINT\u00ae. No deben esperarse de ning\u00fan otro programa, incluso uno descrito como \u201calternativa\u201d, \u201cequivalente\u201d, \u201cguiado por fMRI\u201d o \u201cacelerado\u201d.",
          "Llamarse una alternativa a SAINT\u00ae, o mencionar fMRI, no convierte un programa en SAINT\u00ae ni hace comparables sus resultados.",
          "Para confirmar que una cl\u00ednica ofrece SAINT\u00ae real, consulte el localizador oficial de Magnus Medical: https://www.magnusmedical.com/find-a-provider",
        ].map((item, i) => (
          <li
            key={i}
            className="relative pl-5 leading-relaxed text-foreground before:absolute before:left-0 before:top-2.5 before:h-1.5 before:w-1.5 before:rounded-full before:bg-accent"
          >
            {item}
          </li>
        ))}
      </ul>
    ),
  },
  {
    heading: "Cognitive FX no es SAINT\u00ae, en sus propias palabras",
    body: (
      <>
        <p>No tiene que creernos. El propio sitio web de Cognitive FX dice:</p>
        <ul className="mt-4 space-y-3">
          {quotes.map((q, i) => (
            <li key={i} className="leading-relaxed text-foreground">
              <span>&ldquo;{q.text}&rdquo;</span>{" "}
              <a
                href={q.url}
                target="_blank"
                rel="noopener noreferrer"
                className="text-xs text-accent underline underline-offset-2 hover:opacity-80"
              >
                (fuente)
              </a>
            </li>
          ))}
        </ul>
        <p className="mt-4 text-sm text-muted-foreground">
          Citas consultadas el 3 de noviembre de 2026. Si Cognitive FX cambia su
          redacci\u00f3n, actualizaremos esta p\u00e1gina.
        </p>
      </>
    ),
  },
  {
    heading:
      "Qu\u00e9 NO significan \u201calternativa\u201d, \u201cequivalente\u201d y \u201cguiado por fMRI\u201d",
    body: (
      <>
        <p>
          <strong>
            &ldquo;Alternativa&rdquo; o &ldquo;equivalente&rdquo;:
          </strong>{" "}
          Que una cl\u00ednica llame a su programa una alternativa o equivalente
          de SAINT\u00ae es una descripci\u00f3n de marketing. No significa que
          el programa sea SAINT\u00ae, que est\u00e9 autorizado por la FDA como
          SAINT\u00ae, o que produzca los resultados de SAINT.
        </p>
        <p>
          <strong>&ldquo;Guiado por fMRI&rdquo;:</strong> SAINT\u00ae es el
          \u00fanico sistema autorizado por la FDA que usa una resonancia fMRI
          para personalizar d\u00f3nde se dirige la TMS (FDA 510(k) K220177).
          Otros programas que mencionan fMRI usan su propia orientaci\u00f3n,
          que no est\u00e1 autorizada por la FDA para este fin. Incluir una
          resonancia fMRI en el programa no lo convierte en SAINT\u00ae.
        </p>
        <p>
          <strong>Mismo calendario \u2260 mismo tratamiento:</strong> Copiar los
          5 d\u00edas y el n\u00famero de sesiones de SAINT no copia la
          orientaci\u00f3n autorizada por la FDA que probaron los ensayos. Un
          programa que no se estudi\u00f3 en los ensayos de SAINT no puede
          reclamar los resultados de esos ensayos.
        </p>
      </>
    ),
  },
  {
    heading: "Aviso sobre resultados",
    body: (
      <p>
        Las tasas de respuesta y remisi\u00f3n de SAINT\u00ae publicadas en
        investigaci\u00f3n revisada por pares (Cole et al., Am J Psychiatry
        2022;179:132\u2013141) aplican \u00fanicamente a SAINT\u00ae aplicado
        con el sistema Magnus autorizado por la FDA bajo el protocolo SAINT. No
        se aplican a, ni deben usarse para fijar expectativas sobre, ning\u00fan
        programa de TMS off-label, similar, &ldquo;guiado por fMRI&rdquo;,
        &ldquo;acelerado&rdquo; o &ldquo;alternativa&rdquo;, incluido el de
        Cognitive FX. Los resultados individuales con SAINT\u00ae tambi\u00e9n
        var\u00edan, y no se garantiza ning\u00fan resultado.
      </p>
    ),
  },
  {
    heading: "Aviso sobre precios: los precios no se pueden comparar",
    body: (
      <>
        <p>
          Cognitive FX publica $9.900 por su programa de 5 d\u00edas y
          se\u00f1ala que viaje y alojamiento son aparte. Ese es el precio de un
          tratamiento distinto y off-label, no de un SAINT\u00ae con descuento.
          SAINT\u00ae en Neuro Wellness comienza en $25.000 por el curso
          completo autorizado por la FDA, tiene una tarifa de Medicare, y los
          planes comerciales pueden aprobarlo mediante autorizaci\u00f3n previa.
          Verificamos sus beneficios sin costo.
        </p>
        <p className="mt-3">
          Vea{" "}
          <Link
            to="/es/learn/saint-tms-cost"
            className="font-medium text-accent underline underline-offset-2 hover:opacity-80"
          >
            Costo de SAINT\u00ae TMS
          </Link>{" "}
          y{" "}
          <Link
            to="/es/learn/cognitive-fx-vs-saint-tms"
            className="font-medium text-accent underline underline-offset-2 hover:opacity-80"
          >
            Cognitive FX vs SAINT\u00ae TMS
          </Link>
          .
        </p>
      </>
    ),
  },
  {
    heading: "Nuestra declaraci\u00f3n de no afiliaci\u00f3n y marcas",
    body: (
      <p>
        Neuro Wellness TMS Centers of America es un proveedor verificado de
        SAINT\u00ae que figura en el localizador de Magnus Medical. No estamos
        afiliados a, avalados por ni conectados con Cognitive FX. Cognitive FX
        no est\u00e1 afiliada con SAINT\u00ae, Stanford University ni Magnus
        Medical: el propio sitio web de Cognitive FX afirma que &ldquo;does not
        provide SAINT\u00ae and is not affiliated with, endorsed by or licensed
        by Stanford University or Magnus Medical.&rdquo; SAINT\u00ae es una
        marca registrada de Stanford University, con licencia exclusiva a Magnus
        Medical. Cognitive FX se nombra aqu\u00ed solo para identificar a la
        empresa y citar sus declaraciones p\u00fablicas. Esta p\u00e1gina es
        para educaci\u00f3n del paciente y no es consejo m\u00e9dico.
      </p>
    ),
  },
];

const relatedLinks: RL[] = [
  {
    title:
      "Cognitive FX frente a SAINT\u00ae TMS: \u00bfcu\u00e1l es la diferencia?",
    href: "/es/learn/cognitive-fx-vs-saint-tms",
    description:
      "Comparaci\u00f3n: estado ante la FDA, orientaci\u00f3n, evidencia, precio y seguro",
  },
  {
    title: 'SAINT\u00ae frente a la "TMS acelerada" de menor costo',
    href: "/es/learn/saint-vs-accelerated-tms",
    description:
      "Qu\u00e9 es igual, qu\u00e9 no, y qu\u00e9 cubre la autorizaci\u00f3n de la FDA",
  },
  {
    title:
      "\u00bfEs realmente SAINT\u00ae? C\u00f3mo verificar una cl\u00ednica",
    href: "/es/learn/what-is-a-verified-saint-provider",
    description:
      "Una verificaci\u00f3n de 3 pasos y se\u00f1ales de alerta antes de pagar",
  },
  {
    title: "Costo de SAINT\u00ae TMS",
    href: "/es/learn/saint-tms-cost",
    description: "Qu\u00e9 cuesta SAINT\u00ae, Medicare y opciones de pago",
  },
  {
    title: "TMS Ampa One\u2122 en Florida",
    href: "/es/ampa",
    description:
      "TMS con c\u00e1mara y acelerada para la depresi\u00f3n en Coral Springs",
  },
  {
    title: "SAINT\u00ae TMS acelerada",
    href: "/es/saint",
    description: "El protocolo de 5 d\u00edas guiado por fMRI que ofrecemos",
  },
];

export const esSaintDisclaimer: LearnPageData = {
  slug: "saint-disclaimer",
  title:
    "Aviso de SAINT\u00ae: Cognitive FX y otros programas \u201cguiados por fMRI\u201d no son SAINT\u00ae",
  description:
    'Cognitive FX y otros programas de "TMS acelerada guiada por fMRI" no son SAINT\u00ae ni est\u00e1n afiliados a \u00e9l. Los resultados de SAINT\u00ae no se aplican a ellos. Lea los hechos.',
  canonical: "/es/learn/saint-disclaimer",
  h1: "Aviso de SAINT\u00ae: los programas parecidos no son SAINT\u00ae",
  breadcrumbLabel: "Aviso de SAINT\u00ae",
  crumbs: esBaseCrumbs("Aviso de SAINT\u00ae"),
  shortAnswer:
    "Cognitive FX no ofrece SAINT\u00ae. Su programa de 5 d\u00edas de \u201cTMS acelerada guiada por fMRI\u201d es un tratamiento distinto y off-label, y Cognitive FX no est\u00e1 afiliada a SAINT\u00ae, Magnus Medical, Stanford University ni Neuro Wellness TMS Centers of America. Los resultados publicados de SAINT \u2014incluido el 78,6 % de remisi\u00f3n en el ensayo controlado aleatorizado de 2022\u2014 provienen \u00fanicamente de SAINT\u00ae y no deben esperarse de ning\u00fan programa parecido.",
  keyTakeaways: [
    "Cognitive FX no ofrece SAINT\u00ae. Su programa de 5 d\u00edas de \u201cTMS acelerada guiada por fMRI\u201d es un tratamiento distinto.",
    "Cognitive FX afirma que \u201cno est\u00e1 afiliada, avalada ni autorizada por Stanford University ni Magnus Medical\u201d y que \u201cno usa equipos ni software de Magnus Medical.\u201d",
    "El 78,6 % de remisi\u00f3n de SAINT (Cole et al., 2022) proviene solo de SAINT\u00ae en el sistema Magnus autorizado por la FDA, no de ning\u00fan programa \u201calternativo\u201d o \u201cguiado por fMRI\u201d.",
    "Copiar el calendario de 5 d\u00edas de SAINT no copia la orientaci\u00f3n autorizada por la FDA que probaron los ensayos.",
    "Neuro Wellness en Coral Springs, Florida, es el proveedor verificado de SAINT\u00ae que figura en el localizador de Magnus Medical.",
  ],
  sections,
  faqs,
  sources,
  schemaJson: {
    "@context": "https://schema.org",
    "@graph": [
      {
        ...webPageNode({
          id: `${SITE_URL}/es/learn/saint-disclaimer#webpage`,
          name: "Aviso de SAINT\u00ae: los programas parecidos no son SAINT\u00ae",
          url: `${SITE_URL}/es/learn/saint-disclaimer`,
          description:
            'Cognitive FX y otros programas de "TMS acelerada guiada por fMRI" no son SAINT\u00ae ni est\u00e1n afiliados a \u00e9l. Los resultados de SAINT\u00ae no se aplican a ellos. Lea los hechos.',
        }),
        inLanguage: "es-US",
      },
      orgNode,
      faqJsonld(faqs, `${SITE_URL}/es/learn/saint-disclaimer#faq`),
      breadcrumbJsonld(esBaseCrumbs("Aviso de SAINT\u00ae")),
      {
        "@type": "Article",
        "@id": `${SITE_URL}/es/learn/saint-disclaimer#article`,
        headline:
          "Aviso de SAINT\u00ae: los programas parecidos no son SAINT\u00ae",
        description:
          'Cognitive FX y otros programas de "TMS acelerada guiada por fMRI" no son SAINT\u00ae ni est\u00e1n afiliados a \u00e9l. Los resultados de SAINT\u00ae no se aplican a ellos. Lea los hechos.',
        datePublished: "2026-11-03",
        dateModified: "2026-11-03",
        mainEntityOfPage: `${SITE_URL}/es/learn/saint-disclaimer`,
        author: {
          "@type": "MedicalOrganization",
          name: "Neuro Wellness TMS Centers of America",
        },
        reviewedBy: {
          "@type": "Person",
          name: "Daniel Pietras, MD",
          url: `${SITE_URL}/dr-daniel-pietras-md`,
        },
        publisher: {
          "@type": "MedicalOrganization",
          name: "Neuro Wellness TMS Centers of America",
          url: `${SITE_URL}/`,
        },
      },
    ],
  },
  relatedLinks,
  reviewBlock,
  toc: [
    { label: "La versi\u00f3n breve", href: "#la-version-breve" },
    {
      label: "Cognitive FX en sus propias palabras",
      href: "#cognitive-fx-no-es-saint-en-sus-propias-palabras",
    },
    {
      label: "Lo que no significan las etiquetas",
      href: "#que-no-significan-alternativa-equivalente-y-guiado-por-fmri",
    },
    { label: "Aviso sobre resultados", href: "#aviso-sobre-resultados" },
    {
      label: "Aviso sobre precios",
      href: "#aviso-sobre-precios-los-precios-no-se-pueden-comparar",
    },
    {
      label: "No afiliaci\u00f3n y marcas",
      href: "#nuestra-declaracion-de-no-afiliacion-y-marcas",
    },
    { label: "Preguntas frecuentes", href: "#faq" },
    { label: "Investigaci\u00f3n citada", href: "#cited-research" },
  ],
  safetyBlock:
    "Los resultados individuales var\u00edan. No se garantiza ning\u00fan resultado. La cobertura var\u00eda seg\u00fan el plan. Si est\u00e1 en crisis, llame o envie un mensaje de texto al 988.",
  trademarkFooter:
    "Cognitive FX es una marca de su propietario. SAINT\u00ae es una marca registrada de Stanford University, con licencia exclusiva a Magnus Medical, Inc. Neuro Wellness TMS Centers of America es un proveedor verificado de SAINT\u00ae y no est\u00e1 afiliada a, avalada por ni conectada con Cognitive FX. Las citas de los materiales publicados de Cognitive FX se consultaron el 3 de noviembre de 2026.",
  ctaBand: {
    headline: "Vea si el SAINT\u00ae real es para usted, sin costo",
    primaryText: "Vea si puedo ser candidato",
    primaryHref: "/es/learn/who-qualifies-for-saint-tms",
    secondaryText: "Hable con una persona",
    secondaryHref: "tel:9548279793",
    phone: "954-827-9793",
  },
};
